/**
 * DeferredVerificationCard
 *
 * Shown on the dashboard when the client has unverified leads waiting (set by
 * cancelling the VerifyBatchModal at upload time, or any phones the client
 * deferred from a partial verification run).
 *
 * Displays the count + a "Run Now" button that opens a modal letting the
 * client pick a batch size (5,000 minimum). Submitting calls
 * runDeferredVerification + createCheckoutSession and redirects to Stripe.
 */

import { useEffect, useState, FormEvent } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';

const MIN_BATCH = 5000;

interface Props {
  clientId: string | null;
  billingType: 'per_call' | 'per_contact';
}

export function DeferredVerificationCard({ clientId, billingType }: Props) {
  const [count, setCount] = useState(0);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(doc(db, 'clients', clientId), snap => {
      if (snap.exists()) {
        setCount((snap.data().deferredVerificationQueue as number) || 0);
      }
    });
    return () => unsub();
  }, [clientId]);

  if (count === 0) return null;

  return (
    <>
      <div style={cardStyle}>
        <div>
          <div style={{ color: 'var(--gray-400)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Deferred Verification Queue
          </div>
          <div style={{ marginTop: '4px', fontSize: '1.1rem', color: 'var(--white)' }}>
            <strong style={{ color: 'var(--gold)' }}>{count.toLocaleString()}</strong>
            <span style={{ color: 'var(--gray-300)' }}> {count === 1 ? 'lead' : 'leads'} waiting to be verified</span>
          </div>
          <div style={{ marginTop: '6px', fontSize: '0.8rem', color: 'var(--gray-400)' }}>
            These were uploaded but skipped verification &mdash; ready when you are.
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary"
          style={{ padding: '8px 18px' }}
        >
          Run Verification
        </button>
      </div>

      {showModal && (
        <RunDeferredModal
          totalAwaiting={count}
          billingType={billingType}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

interface ModalProps {
  totalAwaiting: number;
  billingType: 'per_call' | 'per_contact';
  onClose: () => void;
}

function RunDeferredModal({ totalAwaiting, billingType, onClose }: ModalProps) {
  const isBelowMin = totalAwaiting < MIN_BATCH;
  const [batchSize, setBatchSize] = useState<number>(isBelowMin ? totalAwaiting : Math.min(MIN_BATCH, totalAwaiting));
  const [includeReverse, setIncludeReverse] = useState(billingType === 'per_contact');
  const [cellOnly, setCellOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState<'idle' | 'preparing' | 'checkout' | 'redirecting'>('idle');
  const [error, setError] = useState<string | null>(null);

  const reverseLocked = billingType === 'per_contact';

  const stageLabel = (() => {
    switch (stage) {
      case 'preparing': return 'Preparing your batch...';
      case 'checkout': return 'Opening secure checkout...';
      case 'redirecting': return 'Redirecting to Stripe...';
      default: return null;
    }
  })();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isBelowMin && batchSize < MIN_BATCH) {
      setError(`Minimum batch size is ${MIN_BATCH.toLocaleString()}`);
      return;
    }
    setSubmitting(true);
    setError(null);
    setStage('preparing');
    try {
      // Single callable — runDeferredVerification now creates the Stripe
      // checkout session inline and returns the sessionUrl directly. This
      // saves one cold start (~5-10s) compared to two separate calls.
      const runFn = httpsCallable(functions, 'runDeferredVerification');
      const runResult = await runFn({
        count: batchSize,
        includeReverse,
        cellOnly,
      });
      const { jobId, sessionUrl: inlineUrl } = runResult.data as { jobId: string; sessionUrl?: string };

      let sessionUrl = inlineUrl;
      if (!sessionUrl) {
        // Fallback path: inline checkout failed, retry via the dedicated callable
        setStage('checkout');
        const checkoutFn = httpsCallable(functions, 'createCheckoutSession');
        const checkoutResult = await checkoutFn({
          tierId: 'verification',
          phoneBatchJobId: jobId,
        });
        sessionUrl = (checkoutResult.data as { sessionUrl: string }).sessionUrl;
      }

      // Redirect
      setStage('redirecting');
      window.location.href = sessionUrl!;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      setSubmitting(false);
      setStage('idle');
    }
  };

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content" style={contentStyle}>
        <h2 style={{ margin: '0 0 16px', color: 'var(--gold)' }}>Run Deferred Verification</h2>

        <p style={{ color: 'var(--gray-200)', lineHeight: 1.6, margin: '0 0 16px' }}>
          You have <strong style={{ color: 'var(--gold)' }}>{totalAwaiting.toLocaleString()}</strong> leads
          waiting to be verified.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label>Batch size</label>
            {isBelowMin ? (
              <>
                <input type="number" value={totalAwaiting} disabled />
                <div style={{ fontSize: '0.8rem', color: 'var(--gold)', marginTop: '4px' }}>
                  Below the {MIN_BATCH.toLocaleString()} minimum &mdash; running all at once.
                </div>
              </>
            ) : (
              <>
                <input
                  type="number"
                  value={batchSize}
                  min={MIN_BATCH}
                  max={totalAwaiting}
                  step={1000}
                  onChange={e => setBatchSize(parseInt(e.target.value) || MIN_BATCH)}
                  disabled={submitting}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setBatchSize(MIN_BATCH)} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}>5,000</button>
                  <button type="button" onClick={() => setBatchSize(Math.min(10000, totalAwaiting))} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}>10,000</button>
                  <button type="button" onClick={() => setBatchSize(Math.min(25000, totalAwaiting))} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}>25,000</button>
                  <button type="button" onClick={() => setBatchSize(totalAwaiting)} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}>Run All</button>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '4px' }}>
                  Minimum {MIN_BATCH.toLocaleString()} per batch.
                </div>
              </>
            )}
          </div>

          <label style={toggleLabelStyle}>
            <input
              type="checkbox"
              checked={includeReverse}
              disabled={reverseLocked || submitting}
              onChange={e => setIncludeReverse(e.target.checked)}
              style={{ accentColor: 'var(--gold)' }}
            />
            <span>
              <strong>Include reverse phone name check</strong>
              {reverseLocked && <span style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}> &mdash; required for per-contact billing</span>}
            </span>
          </label>
          <label style={{ ...toggleLabelStyle, marginTop: '8px' }}>
            <input
              type="checkbox"
              checked={cellOnly}
              disabled={submitting}
              onChange={e => setCellOnly(e.target.checked)}
              style={{ accentColor: 'var(--gold)' }}
            />
            <span>
              <strong>Cell phones only</strong>
              <span style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}> &mdash; skip landlines during reverse phone</span>
            </span>
          </label>

          {error && (
            <div style={{ marginTop: '16px', padding: '12px', borderRadius: '6px', background: 'var(--red-bg)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.3)' }}>
              {error}
            </div>
          )}

          {submitting && stageLabel && (
            <div style={progressBoxStyle}>
              <span style={spinnerStyle} />
              <span>{stageLabel}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button type="button" onClick={onClose} disabled={submitting} className="btn btn-secondary">
              Close
            </button>
            <button type="submit" disabled={submitting} className="btn btn-primary">
              {submitting ? 'Processing...' : 'Submit & Pay'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// CSS keyframes for the spinner — injected once via a <style> tag at module load
if (typeof document !== 'undefined' && !document.getElementById('jad-spinner-keyframes')) {
  const style = document.createElement('style');
  style.id = 'jad-spinner-keyframes';
  style.textContent = `@keyframes jad-spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}

const spinnerStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '16px',
  height: '16px',
  border: '2px solid rgba(200, 168, 78, 0.25)',
  borderTopColor: 'var(--gold)',
  borderRadius: '50%',
  animation: 'jad-spin 0.8s linear infinite',
  verticalAlign: 'middle',
};

const progressBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginTop: '16px',
  padding: '12px 16px',
  borderRadius: '8px',
  background: 'rgba(200, 168, 78, 0.08)',
  border: '1px solid rgba(200, 168, 78, 0.3)',
  color: 'var(--gray-200)',
  fontSize: '0.9rem',
};

const cardStyle: React.CSSProperties = {
  padding: '14px 18px',
  marginBottom: '12px',
  borderRadius: '10px',
  background: 'var(--dark-2)',
  border: '1px solid rgba(200, 168, 78, 0.4)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
};

const contentStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #1e1e1e)', borderRadius: '12px',
  padding: '32px', maxWidth: '520px', width: '100%',
  border: '1px solid rgba(200, 168, 78, 0.3)',
  maxHeight: '90vh', overflowY: 'auto',
};

const toggleLabelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: '10px',
  cursor: 'pointer', color: 'var(--gray-200)', fontSize: '0.95rem',
  padding: '8px', borderRadius: '6px',
};
